import i18next from 'i18next';
import { getStatusStr } from '../translations';
import {
  GoalBase,
  GoalEmployee,
  GoalStatus,
  GoalViewBy,
  NULL_CATEGORY_ID,
} from '../types';

type GroupKey = {
  id: string;
  name: string;
};

const STATUS_ORDER: GoalStatus[] = [
  'DRAFT',
  'BEHIND',
  'ON_TRACK',
  'DONE',
  'CANCELED',
];

// Group and sort goals by category and statuses
export function groupGoals(
  goals: GoalBase[],
  viewBy: GoalViewBy,
): Map<string, GoalBase[]> {
  const sortedGoals = [...goals].sort((a, b) => compareGoal(a, b, viewBy));
  return sortedGoals.reduce((acc, goal) => {
    const groupKey =
      viewBy === 'status'
        ? { id: goal.status, name: getStatusStr(goal.status) }
        : {
            id: goal.category?.id ?? NULL_CATEGORY_ID,
            name:
              goal.category?.name ??
              i18next.t('performanceReview.goals2.goalsFilter.noCategory') ??
              '',
          };
    const groupKeyStr = serializeGroupKey(groupKey);
    acc.set(groupKeyStr, [...(acc.get(groupKeyStr) || []), goal]);
    return acc;
  }, new Map<string, GoalBase[]>());
}

export function getGroupName(groupKeyStr: string): string {
  const groupKey = deserializeGroupKey(groupKeyStr);
  return groupKey.name;
}

export function getGroupId(groupKeyStr: string): string {
  const groupKey = deserializeGroupKey(groupKeyStr);
  return groupKey.id;
}

const getStatusOrder = (status: GoalStatus) => {
  const idx = STATUS_ORDER.indexOf(status);
  return idx === -1 ? STATUS_ORDER.length : idx;
};

const compareGoal = (a: GoalBase, b: GoalBase, viewBy: GoalViewBy) => {
  if (viewBy === 'category') {
    const categoryCompare = compareGoalByCategory(a, b);
    if (categoryCompare !== 0) {
      return categoryCompare;
    }
    return getStatusOrder(a.status) - getStatusOrder(b.status);
  } else if (viewBy === 'status') {
    const statusOrder = getStatusOrder(a.status) - getStatusOrder(b.status);
    if (statusOrder !== 0) {
      return statusOrder;
    }
    return compareGoalByCategory(a, b);
  }
  return 0;
};

const compareGoalByCategory = (a: GoalBase, b: GoalBase) => {
  const hasA = a.category != null;
  const hasB = b.category != null;
  if (!hasA && hasB) return 1;
  if (hasA && !hasB) return -1;
  if (!hasA && !hasB) return 0;

  const orderA = a.category?.order ?? Number.MAX_SAFE_INTEGER;
  const orderB = b.category?.order ?? Number.MAX_SAFE_INTEGER;
  if (orderA !== orderB) return orderA - orderB;

  const nameA = a.category?.name?.toLowerCase() ?? '';
  const nameB = b.category?.name?.toLowerCase() ?? '';
  if (nameA !== nameB) return nameA.localeCompare(nameB);

  return 0;
};

type CategoryAssignment = {
  categoryId: string;
  weight?: number | null;
  category: {
    Name: string;
    flair__Order__c?: number | null;
  };
};

/**
 * When categories have weights, ensure all assigned categories appear as groups
 * (even if they have no goals), sorted by order then name.
 */
export function ensureAllCategoryGroups(
  grouped: Map<string, GoalBase[]>,
  assignments: readonly CategoryAssignment[],
): Map<string, GoalBase[]> {
  const sorted = [...assignments]
    .filter((a) => a.weight != null)
    .sort((a, b) => {
      const orderA = a.category.flair__Order__c ?? Number.MAX_SAFE_INTEGER;
      const orderB = b.category.flair__Order__c ?? Number.MAX_SAFE_INTEGER;
      if (orderA !== orderB) return orderA - orderB;
      return (a.category.Name ?? '').localeCompare(b.category.Name ?? '');
    });

  const result = new Map<string, GoalBase[]>();
  for (const assignment of sorted) {
    const key = serializeGroupKey({
      id: assignment.categoryId,
      name: assignment.category.Name,
    });
    result.set(key, grouped.get(key) ?? []);
  }

  // Preserve any groups not covered by assignments (e.g. "No Category")
  for (const [key, goals] of grouped) {
    if (!result.has(key)) {
      result.set(key, goals);
    }
  }

  return result;
}

export const DELIMETER = '<@#!:::>';

const serializeGroupKey = (groupKey: GroupKey): string => {
  return `${groupKey.id}${DELIMETER}${groupKey.name}`;
};

const deserializeGroupKey = (serializedKey: string): GroupKey => {
  const [id, name] = serializedKey.split(DELIMETER);
  return { id, name };
};

export type EmployeeGroup = {
  employee: GoalEmployee;
  goals: GoalBase[];
};

export function groupGoalsByEmployee(
  goals: GoalBase[],
): Map<string, EmployeeGroup> {
  const map = new Map<string, EmployeeGroup>();

  for (const goal of goals) {
    const employee = goal.employee;
    if (!employee) {
      continue;
    }
    const existing = map.get(employee.id);
    if (existing) {
      existing.goals.push(goal);
    } else {
      map.set(employee.id, { employee, goals: [goal] });
    }
  }

  const sortedEntries = Array.from(map.entries()).sort(([, a], [, b]) =>
    a.employee.name.toLowerCase().localeCompare(b.employee.name.toLowerCase()),
  );
  return new Map(sortedEntries);
}
